import { useCallback } from 'react';
import { css } from '@emotion/react';
import { paymentsSentry } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import { RadioCardField, useForm, useFormField, useTooltip } from '@frontend/design-system';
import { useCareCreditSmsNumber } from '../../../hooks/use-care-credit-person-sms-number';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { CollectBaseStep } from '../atoms/step/collect-base-step';
import { CareCreditCollectMultiSteps, CareCreditCollectTypes } from '../collect-multi-step.types';
import { useCareCreditCollectContext } from '../context/care-credit-collect.context';

const styles = {
  radioCardField: css`
    & > div {
      flex-direction: column;
    }
  `,
  radioCardFieldDisabled: css`
    margin-top: ${theme.spacing(2)};

    & > div {
      flex-direction: column;
    }
  `,
};
const CURRENT_STEP: CareCreditCollectMultiSteps = 'select-payment-method';
const FORM_STEP: CareCreditCollectMultiSteps = 'payment-method-form';
const PAYMENT_METHOD_FORM_VALUE: CareCreditCollectTypes = 'form';
const PAYMENT_METHOD_TTP_VALUE: CareCreditCollectTypes = 'ttp';

export const Step1SelectPaymentMethod = () => {
  const { t } = useCareCreditTranslations();

  const {
    person,
    locationId,
    onCancel,
    onGoBack,
    onGoToMessageStep,
    multiStep: { goToStep },
  } = useCareCreditCollectContext();

  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'top-start',
  });

  const { firstValidPhoneNumber } = useCareCreditSmsNumber({
    person,
    locationId,
  });

  const { getFieldProps, isComplete, values } = useForm({
    fields: {
      paymentMethod: { type: 'radio', required: true },
    },
  });

  const disabledField = useFormField({
    type: 'radio',
  });

  const onClickCancel = useCallback(() => {
    if (onCancel) {
      return onCancel?.();
    }
    paymentsSentry.error('Cancel clicked but no onCancel handler found in context', 'Step1SelectPaymentMethod', '');
    return console.warn('Cancel clicked but no onCancel handler found in context');
  }, [onCancel]);

  const onClickNext = useCallback(() => {
    if (values.paymentMethod === PAYMENT_METHOD_FORM_VALUE) {
      goToStep({
        orStep: FORM_STEP,
      });
    } else if (values.paymentMethod === PAYMENT_METHOD_TTP_VALUE) {
      onGoToMessageStep();
    } else {
      paymentsSentry.error(
        'Invalid payment method received',
        'Step1SelectPaymentMethod',
        `Received payment method: ${values.paymentMethod}`
      );
      console.warn('Invalid payment method received:', values.paymentMethod);
    }
  }, [values.paymentMethod, onGoToMessageStep, goToStep]);
  return (
    <CollectBaseStep
      id={CURRENT_STEP}
      label={t('How would you like to process the CareCredit payment?')}
      footer={{
        primary: {
          children: t('Next'),
          disabled: !isComplete,
        },
        onClickPrimary: onClickNext,
        secondary: {
          children: t('Cancel'),
        },
        onClickSecondary: onClickCancel,
        back: {
          children: t('Back'),
          iconName: 'back',
        },
        showBackButton: true,
        onClickBack: onGoBack,
      }}
    >
      <RadioCardField
        {...getFieldProps('paymentMethod')}
        name='paymentMethod'
        className='select-payment-method--radio-card-field'
        css={styles.radioCardField}
      >
        <RadioCardField.Option
          value={PAYMENT_METHOD_FORM_VALUE}
          title={t('Submit in-office')}
          description={t('Complete the transaction at patient’s request')}
        />
        {!!firstValidPhoneNumber && (
          <RadioCardField.Option
            value={PAYMENT_METHOD_TTP_VALUE}
            title={t('Send transaction link to patient')}
            description={t('Send link to patient’s mobile phone')}
          />
        )}
      </RadioCardField>
      {!firstValidPhoneNumber && (
        <>
          <div {...triggerProps}>
            <RadioCardField
              {...disabledField}
              name='processMethodDisabled'
              className='select-process-method--radio-card-field'
              css={styles.radioCardFieldDisabled}
              disabled={true}
            >
              <RadioCardField.Option
                value={PAYMENT_METHOD_TTP_VALUE}
                title={t('Send prequalification link')}
                description={t(`Send link to patient's mobile number`)}
              />
              <span></span>
            </RadioCardField>
          </div>
          <Tooltip {...tooltipProps}>{t('No valid phone number')}</Tooltip>
        </>
      )}
    </CollectBaseStep>
  );
};
